import { createContext, useCallback, useContext, useEffect, useId } from 'react'

import { useLatestRef } from '@shared/hooks/src/useLatestRef'

export const LeavePromptContext = createContext(undefined)

export const resolveWhen = (when) => Boolean(typeof when === 'function' ? when() : when)

/**
 * Registers a guard with the nearest `LeavePromptProvider`. While active, a guard asks the
 * browser to confirm before the page unloads and, unless `blocksNavigation` is false, shows the
 * "leave?" dialog for in-app navigation out of its flow. When several blocking guards are active
 * the most recently mounted one wins. Navigations that keep the same pathname never prompt, and
 * neither do history pops, except from back controls wired through `useBackIntercept`.
 *
 * @param {object} guard - The guard config.
 * @param {string} guard.source - Analytics tag and `ui.leavePrompt.<source>.*` copy key.
 * @param {boolean|Function} guard.when - Whether the guard is active. Pass a function only when the value must be read at navigation time.
 * @param {Function} [guard.allow] - `(nextLocation, currentLocation) => boolean`, true lets the navigation through.
 * @param {React.ElementType} [guard.icon] - MUI icon for the dialog hero. Required unless `blocksNavigation` is false.
 * @param {boolean} [guard.blocksNavigation] - False makes the guard unload-only.
 */
export default function useLeavePrompt({ source, when, allow, icon, blocksNavigation = true }) {
  const context = useContext(LeavePromptContext)
  const id = useId()
  const ref = useLatestRef({ source, when, allow, icon, blocksNavigation })

  useEffect(() => {
    if (!context) return
    context.register(id, ref)
    return () => context.unregister(id)
  }, [context, id, ref])
}

/**
 * Click handler for a back control that pops the history stack. When the step back would leave
 * a guarded flow it cancels the click and opens the "leave?" dialog, which performs the step
 * itself if the patient confirms. Controls that go to a fixed path need nothing: the router
 * blocker already covers them.
 *
 * @param {string|number} to - The control's target; only numeric deltas are intercepted.
 * @returns {Function} An `onClick` handler.
 */
export function useBackIntercept(to) {
  const context = useContext(LeavePromptContext)

  return useCallback(
    (event) => {
      if (typeof to === 'number' && context?.intercept(to)) event.preventDefault()
    },
    [context, to]
  )
}
